// client/src/hooks/useAppDownload.js
//
// Desktop-app (Electron) update state for the header's notification bell.
//
// Inside the desktop app, the bell compares the INSTALLED version (exposed by
// the app's preload as window.electronAPI.appVersion, 1.1.0+) with the
// PUBLISHED one from latest.json, and shows "update ready to install" until the
// user is actually on the new version — downloading alone doesn't clear it.
// Desktop builds older than 1.1.0 don't expose a version, so they always count
// as out of date.
//
// In a plain browser there is no installed version to compare, so the bell
// falls back to announcing each new release once (remembered in localStorage).
//
// The published version is re-checked every 30 minutes and when the window
// regains focus, because desktop windows stay open for days. `download()`
// fetches a short-lived presigned S3 URL (.dmg for Macs, .exe otherwise) and
// navigates to it; the backend never streams the binary itself.

import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../services/apiClient';

// localStorage key remembering the last app version this browser has "seen".
export const SEEN_VERSION_KEY = 'appDownloadSeenVersion';

const RECHECK_MS = 30 * 60 * 1000;

/** True on a Mac browser (Intel or Apple Silicon); iPad/iPhone are excluded. */
export const isMacPlatform = (nav = typeof navigator !== 'undefined' ? navigator : undefined) => {
  if (!nav) return false;
  const platform = nav.userAgentData?.platform || nav.platform || '';
  if (/mac/i.test(platform)) return !(nav.maxTouchPoints > 1); // iPadOS reports "MacIntel"
  return /Macintosh|Mac OS X/i.test(nav.userAgent || '') && !(nav.maxTouchPoints > 1);
};

/**
 * What the desktop app (if any) reports about itself.
 * @returns {{ inDesktopApp: boolean, installedVersion: string|null, isMac: boolean }}
 */
export const getDesktopApp = (win = typeof window !== 'undefined' ? window : undefined) => {
  const api = win?.electronAPI;
  if (!api) return { inDesktopApp: false, installedVersion: null, isMac: isMacPlatform() };
  return {
    inDesktopApp: true,
    installedVersion: api.appVersion || null,
    isMac: api.platform ? api.platform === 'darwin' : isMacPlatform(),
  };
};

/**
 * Compare dotted versions numerically: 1.10.0 > 1.9.2. Missing parts count as 0.
 * @returns {number} >0 if a is newer, <0 if b is newer, 0 if equal
 */
export const compareVersions = (a, b) => {
  const pa = String(a ?? '').split('.').map((n) => parseInt(n, 10) || 0);
  const pb = String(b ?? '').split('.').map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i += 1) {
    const d = (pa[i] || 0) - (pb[i] || 0);
    if (d !== 0) return d;
  }
  return 0;
};

/**
 * Should the bell show an update?
 * Desktop app: published is newer than installed (no installed version = a
 * pre-1.1.0 build, so yes). Browser: a release this browser hasn't seen yet.
 */
export const computeHasUpdate = ({ published, inDesktopApp, installedVersion, seenVersion }) => {
  if (!published) return false;
  if (inDesktopApp) return !installedVersion || compareVersions(published, installedVersion) > 0;
  return seenVersion !== published;
};

/**
 * Pick this platform's installer from the /version payload. Mac browsers get
 * the .dmg; when no Mac build is published they get null (no update prompt),
 * never the Windows .exe.
 */
export const selectPlatformInfo = (data, isMac) => {
  if (!data?.version) return null;
  if (!isMac) return { version: data.version, filename: data.filename, releasedAt: data.releasedAt, size: data.size };
  if (!data.macFilename) return null;
  return { version: data.version, filename: data.macFilename, releasedAt: data.releasedAt, size: data.macSize ?? null, platform: 'mac' };
};

// Human-readable file size for the download menu (e.g. "73.8 MB").
export const formatSize = (bytes) => {
  if (!bytes || Number.isNaN(bytes)) return null;
  const mb = bytes / (1024 * 1024);
  return mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${mb.toFixed(1)} MB`;
};

export const useAppDownload = ({ enabled = true } = {}) => {
  const [desktop] = useState(() => getDesktopApp());
  const [info, setInfo] = useState(null);       // { version, filename, releasedAt, size, platform? }
  const [hasUpdate, setHasUpdate] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const check = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/app-download/version');
      if (!data?.success) return;
      const platformInfo = selectPlatformInfo(data, desktop.isMac);
      setInfo(platformInfo);
      setHasUpdate(computeHasUpdate({
        published: platformInfo?.version,
        inDesktopApp: desktop.inDesktopApp,
        installedVersion: desktop.installedVersion,
        seenVersion: localStorage.getItem(SEEN_VERSION_KEY),
      }));
    } catch (error) {
      // Non-fatal: no version info just means no bell/download prompt.
      console.warn('App version check failed:', error?.message);
    }
  }, [desktop]);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    const run = () => { if (!cancelled) check(); };
    run();
    const timer = setInterval(run, RECHECK_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') run(); };
    window.addEventListener('focus', run);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearInterval(timer);
      window.removeEventListener('focus', run);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [check, enabled]);

  // Browser only: a downloaded release stops being "new". Inside the desktop
  // app the bell stays until the update is actually installed.
  const markSeen = () => {
    if (info?.version) {
      localStorage.setItem(SEEN_VERSION_KEY, info.version);
      if (!desktop.inDesktopApp) setHasUpdate(false);
    }
  };

  const download = async () => {
    if (isDownloading) return;
    try {
      setIsDownloading(true);
      const { data } = await apiClient.get('/app-download/url', {
        params: desktop.isMac ? { platform: 'mac' } : undefined,
      });
      if (data?.success && data.url) {
        markSeen();
        // Navigate the browser to the presigned URL to start the download.
        window.location.href = data.url;
      } else {
        alert('Could not start the download. Please try again.');
      }
    } catch (error) {
      console.error('❌ App download failed:', error?.message);
      alert(error?.response?.status === 404 && desktop.isMac
        ? 'The Mac version of the app is not available yet.'
        : 'Could not start the download. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  return {
    info,
    hasUpdate,
    isDownloading,
    download,
    markSeen,
    inDesktopApp: desktop.inDesktopApp,
    installedVersion: desktop.installedVersion,
    isMac: desktop.isMac,
  };
};
